/**
 * What kind of thing is this file?
 *
 * DATA DEFINITION
 *   A FileKind is one of:
 *     - "part"      a single solid model            (.SLDPRT)
 *     - "assembly"  a model made of other models    (.SLDASM)
 *     - "drawing"   a dimensioned 2D sheet          (.SLDDRW)
 *     - "document"  a released/shareable sheet      (.PDF)
 *     - "exchange"  a neutral CAD interchange model (.STEP, .STP, .IGES, .STL)
 *     - "simulation" a solver model                 (.MPH COMSOL)
 *     - "notebook"  executable analysis             (.IPYNB)
 *     - "text"      human-readable source           (.MD, .TXT, .CSV)
 *     - "image"     a raster or vector picture
 *     - "archive"   a bundle of other files         (.ZIP)
 *     - "other"     anything we do not recognise
 *   Interpretation: the engineering ROLE of a file, not merely its format.
 *   Grouping by role is what lets the UI say "47 parts, 46 drawings" — a
 *   sentence an engineer understands — instead of listing raw extensions.
 *
 *   A PreviewKind is one of "pdf" | "step" | "text" | "image" | "none".
 *   Interpretation: which viewer, if any, can render this file in the browser.
 *   SolidWorks formats are proprietary and have no free browser renderer, so
 *   they are "none" — the UI shows a provenance card and offers the companion
 *   PDF or STEP instead of pretending it can draw them.
 */
import { extensionOf, stemOf, dirname } from './path.js';
const KIND_BY_EXT = {
    sldprt: 'part',
    sldasm: 'assembly',
    slddrw: 'drawing',
    pdf: 'document',
    step: 'exchange', stp: 'exchange', iges: 'exchange', igs: 'exchange', stl: 'exchange', x_t: 'exchange',
    mph: 'simulation',
    ipynb: 'notebook',
    md: 'text', txt: 'text', csv: 'text', json: 'text', yml: 'text', yaml: 'text', gitattributes: 'text',
    png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', svg: 'image', webp: 'image',
    zip: 'archive', '7z': 'archive', rar: 'archive', tar: 'archive', gz: 'archive',
};
/** Classify a file by its extension. kindOf : Path -> FileKind */
export const kindOf = (p) => KIND_BY_EXT[extensionOf(p)] ?? 'other';
const PREVIEW_BY_EXT = {
    pdf: 'pdf',
    step: 'step', stp: 'step',
    md: 'text', txt: 'text', csv: 'text', json: 'text', yml: 'text', yaml: 'text', gitattributes: 'text',
    png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', svg: 'image', webp: 'image',
};
/** Which viewer can render this file, if any. previewKindOf : Path -> PreviewKind */
export const previewKindOf = (p) => PREVIEW_BY_EXT[extensionOf(p)] ?? 'none';
/** True when Lattice can show the file's contents in the browser. */
export const isPreviewable = (p) => previewKindOf(p) !== 'none';
/** Plural English label for a FileKind, used in summaries. */
export const labelForKind = (k, n) => {
    const singular = {
        part: 'part', assembly: 'assembly', drawing: 'drawing', document: 'document',
        exchange: 'STEP model', simulation: 'simulation', notebook: 'notebook',
        text: 'text file', image: 'image', archive: 'archive', other: 'file',
    };
    const plural = {
        part: 'parts', assembly: 'assemblies', drawing: 'drawings', document: 'documents',
        exchange: 'STEP models', simulation: 'simulations', notebook: 'notebooks',
        text: 'text files', image: 'images', archive: 'archives', other: 'files',
    };
    return n === 1 ? singular[k] : plural[k];
};
/**
 * The order kinds are listed in a project summary — most-engineering-relevant
 * first, so "parts, assemblies, drawings" leads and "archives" trails.
 */
export const KIND_ORDER = [
    'part', 'assembly', 'drawing', 'document', 'exchange',
    'simulation', 'notebook', 'image', 'text', 'archive', 'other',
];
/**
 * Find the files that are siblings-by-name of `p` — the same stem in the same
 * folder, or in a sibling "Drawing" folder.
 *
 * WHY: a SolidWorks part, its drawing, its exported PDF and its STEP all share
 * a stem ("Cathode_Cup_Support.SLDPRT", ".SLDDRW", "Drawing/….PDF",
 * "….STEP"). When a user opens a file we cannot render, we offer its
 * renderable companions instead of a dead end. That one affordance is the
 * difference between "this viewer is broken" and "this viewer is thoughtful".
 *
 * companionsOf : Path [Path] -> [Path]
 */
export function companionsOf(p, all) {
    const stem = stemOf(p).toLowerCase();
    const dir = dirname(p);
    // A part in "X/" may have its drawing in "X/Drawing/"; a drawing in
    // "X/Drawing/" may have its part in "X/". Accept both directions.
    const near = (other) => {
        const od = dirname(other);
        if (od === dir)
            return true;
        if (dir && od === `${dir}/Drawing`)
            return true;
        if (od && dir === `${od}/Drawing`)
            return true;
        if (dir && od && dirname(dir) === od)
            return true;
        return false;
    };
    return all.filter(o => o !== p && stemOf(o).toLowerCase() === stem && near(o));
}
//# sourceMappingURL=filekind.js.map